import { useEffect, useId, useRef, useState, type ComponentProps } from "react";
import { Skeleton } from "../../skeleton";
import { useBoundField } from "../field-state";
import { Text } from "@blycare/polar-ui";

export type SelectOption<T extends string | number> = {
  value: T;
  label: string;
  /** 选项的示意色块，给语义色这类"名字看不出颜色"的选项用。 */
  swatch?: string;
};

/** 色块：外圈一道内阴影，浅色和透明色都能看出边界。 */
function Swatch({ color, className }: { color: string; className?: string }) {
  return (
    <span
      className={className}
      style={{
        display: "block",
        flexShrink: 0,
        inlineSize: "16px",
        blockSize: "16px",
        borderRadius: "4px",
        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.16) inset",
        background: color,
      }}
    />
  );
}

/**
 * 触发器 + 浮层列表。浮层的开合、外部点击和焦点交给 s-popover / s-clickable
 * （跟 ColorField 的取色浮层同一套），列表内容自己画——原生 s-select 只能放纯文本，
 * 选项要带颜色之类的富内容就得自己画。
 */
export function SelectField<T extends string | number>({
  label,
  options,
  disabled,
  loading = false,
  skeletonClassName = "h-8",
  icon,
  onChange,
}: {
  label: string;
  options: readonly SelectOption<T>[];
  disabled: boolean;
  /** 数据还没到时只画占位骨架。 */
  loading?: boolean;
  skeletonClassName?: string;
  /** 触发器文字左侧的图标。 */
  icon?: ComponentProps<"s-icon">["type"];
  /** 选完除了写回表单，还要立刻做点什么时用（比如切语言）。 */
  onChange?: (value: T) => void;
}) {
  const { field, handleBlur } = useBoundField<T>();
  const popoverId = useId().replace(/:/g, "");
  // 浮层要跟触发器同宽。s-clickable 是 display:contents，自己量不到宽度，
  // 所以在外面套一层盒子当测量对象，宽度变了同步给浮层。
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);
  useEffect(() => {
    const box = triggerRef.current;
    if (!box) return;
    const observer = new ResizeObserver(() => setTriggerWidth(box.offsetWidth));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  if (loading) return <Skeleton className={skeletonClassName} />;
  const current = options.find((option) => option.value === field.state.value);
  const choose = (value: T) => {
    field.handleChange(value);
    handleBlur();
    onChange?.(value);
  };

  return (
    <>
      {/* h-8：同行字段报错变高时，网格默认的 stretch 会把这个盒子一起撑高，边框也跟着变高。 */}
      <div ref={triggerRef} className="group relative h-8">
        <s-clickable
          commandFor={popoverId}
          type="button"
          blockSize="32px"
          inlineSize="100%"
          paddingInline="small"
          border="none"
          borderRadius="base"
          disabled={disabled}
          accessibilityLabel={`${label}: ${current?.label ?? ""}`}
        >
          {/* 色块和文字先成组再跟勾号分列：三个子元素直接 space-between 会把文字挤到中间。 */}
          <div className="flex h-full w-full items-center justify-between">
            <span className="flex min-w-0 items-center gap-0.5">
              {icon ? <s-icon type={icon} /> : null}
              {current?.swatch ? <Swatch color={current.swatch} className="me-1" /> : null}
              <s-text>{current?.label ?? ""}</s-text>
            </span>
            <s-icon type="select" color="subdued" />
          </div>
        </s-clickable>
        <span className="pointer-events-none absolute inset-0 rounded-lg shadow-[inset_0_0_0_0.66px_#8A8A8A] group-hover:shadow-[inset_0_0_0_0.66px_#616161]" />
      </div>
      {/* 还没量到宽度时用 "auto"（浮层默认值），量到后按触发器宽度写死。 */}
      <s-popover id={popoverId} inlineSize={triggerWidth ? `${triggerWidth}px` : "auto"}>
        <s-box padding="small-300">
          <s-stack gap="small-400">
            {options.map((option) => {
              const selected = option.value === field.state.value;
              return (
                <s-clickable
                  key={option.value}
                  type="button"
                  // 选完顺手把浮层收起来。
                  commandFor={popoverId}
                  command="--hide"
                  paddingInline="small-300"
                  paddingBlock="small-300"
                  borderRadius="base"
                  background={selected ? "subdued" : "transparent"}
                  accessibilityLabel={`${option.label}${selected ? ", selected" : ""}`}
                  onClick={() => choose(option.value)}
                >
                  {/* 三列：色块 | 文案（占满剩余宽度）| 勾号。没有的列不渲染，也就不会留下间隙。 */}
                  <div className="flex w-full min-w-0 items-center gap-2">
                    {option.swatch ? <Swatch color={option.swatch} /> : null}
                    <span className="min-w-0 flex-1">
                      {selected ? (
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {option.label}
                        </Text>
                      ) : (
                        <s-text>{option.label}</s-text>
                      )}
                    </span>
                    {selected ? <s-icon type="check" /> : null}
                  </div>
                </s-clickable>
              );
            })}
          </s-stack>
        </s-box>
      </s-popover>
    </>
  );
}
